import { Drawer, DrawerBody, DrawerHeader, DrawerHeaderTitle, Subtitle2, Title1, Text, TabList, TabValue, Tab, Spinner, Button } from "@fluentui/react-components";
import { Tender, Tendering, TenderManagerTool } from "../../models/tendering";
import { useEffect, useState } from "react";
import TenderInfo from "./Tabs/TenderInfo";
import { getTenderDocument, getTenderingDocument } from "../../services/tenderingService";
import TenderFacts from "./Tabs/TenderFacts";
import TenderLogs from "./Tabs/TenderLogs";
import TenderHistory from "./Tabs/TenderHistory";
import TenderContract from "./Tabs/TenderContract";
import { DismissRegular } from "@fluentui/react-icons";
import { useAppContext } from "../../AppContext";
import { TenderManagerModal } from "./Tabs/TenderManager";
import TenderPermissions from "./Tabs/TenderPermissions";
import TenderHiddenFacts from "./Tabs/TenderHiddenFacts";
import { User } from "../../models/users";
import { Tag } from "../../models/tag";
import { getUsersByModule } from "../../services/usersService";

type Props = {
  onClose: () => void;
  partialTender: any;
  open: boolean;
  tab: string;
  tenderManager?: TenderManagerTool;
  users?: any[];
  tags?: Tag[];
  onTenderManagerChange?: (tenderManagerTool: TenderManagerTool) => void;
  onFilterAdd?: (filter: string) => void;
};

const TenderSettingsDrawer = (props: Props) => {
  const [tender, setTender] = useState<Tender>();
  const [tendering, setTendering] = useState<Tendering>();
  const [actualTab, setActualTab] = useState<TabValue>(props.tab as TabValue);
  const [tenderLoading, setTenderLoading] = useState<boolean>(true);
  const [isProcurer, setIsProcurer] = useState<boolean>(false);
  const [users, setUsers] = useState<User[]>([]);
  const [featurePermissions, setFeaturePermissions] = useState<any[] | null>([]);
  const [overviewQuestionID, setOverviewQuestionID] = useState<number | undefined>(undefined);

  const {user, featurePermissions: userFeatures, getFeaturePermissionsByModuleID} = useAppContext();

  useEffect(() => {
    if(props.open){
        const procurerStatus = user.organization && user.organization.organizationID > 2;
        setIsProcurer(procurerStatus);
        
        // Calculate feature permissions first
        const permissions = procurerStatus ? userFeatures : (getFeaturePermissionsByModuleID(props.partialTender.tenderID ? props.partialTender.tenderID : props.partialTender.moduleID));
        setFeaturePermissions(permissions);
        console.log(permissions);
        
        setTenderLoading(true);
        const fetchTender = async () => {
          try{
            const fetchedTender = await getTenderDocument(props.partialTender.tenderID ? props.partialTender.tenderID : props.partialTender.moduleID);
            setTender(fetchedTender);
            
            // Fetch tendering immediately after getting tender
            const tenderingProperties: string[] = ["content.properties.contact", 
              "content.properties.overviewQuestion",
              "content.tenderingFeeReports",
              "content.properties.factSettings",
              "content.tenderingParts",
              "content.indexing",
              "content.properties.questionMappings"]
            
            const fetchedTendering = await getTenderingDocument(fetchedTender.tenderingID, tenderingProperties);
            setTendering(fetchedTendering);
          }
          catch(error){
            console.error("Error fetching documents:", error);
          }
          finally{
            setTenderLoading(false);
          }
        };
        const fetchTenderUsers = async () => {
          try{
            const userRes = await getUsersByModule(props.partialTender.tenderID ? props.partialTender.tenderID : props.partialTender.moduleID);
            const adminUser = await getUsersByModule(1);
            setUsers([...userRes, ...adminUser]);
          }
          catch(error){
            console.error("Error fetching users:", error);
          }
        }
        // Use the calculated permissions value, not the state
        if(permissions?.some(fp => fp.action == 'Tender page/Changes')){
          fetchTenderUsers();
        }
        fetchTender();
    }
  }, [props.open, props.partialTender]);

  const onTabChange = (tabValue: TabValue, questionID?: number) => {
    if (tabValue === 'facts' && questionID !== undefined) {
      setOverviewQuestionID(questionID);
    } else if (tabValue === 'facts') {
      setOverviewQuestionID(undefined);
    }
    setActualTab(tabValue);
  }

  return (
    <Drawer
      size="large"
      position="end"
      open={props.open}
      modalType="non-modal"
      onOpenChange={(_event, data) => !data.open && props.onClose()}
    >
      <DrawerHeader>
        <DrawerHeaderTitle>
          <div className="flex items-start justify-between w-full">
            <div className="flex flex-col">
              <Text>
                {tenderLoading && tendering ? "" : tendering?.tenderingName}
              </Text>
              {props.partialTender && <Title1>
                {props.partialTender.tenderName ? props.partialTender.tenderName : props.partialTender.moduleName}
              </Title1>}
            </div>
            <Button size="small" icon={<DismissRegular />} shape="circular" onClick={props.onClose}>
            </Button>
          </div>
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>
          {!tenderLoading && <div className="flex flex-col gap-5 flex-1 min-h-0">
            <TabList 
              selectedValue={actualTab}
              onTabSelect={(_, data) => onTabChange(data.value as TabValue)}
              className="flex-none"
            >
              {props.tenderManager && <Tab value="tenderManager">Huvudmeny</Tab>}
              <Tab value="info">Översikt</Tab>
              {featurePermissions?.some(fp => fp.action == 'Tender page/Facts') && <Tab value="facts">Fakta</Tab>}
              {featurePermissions?.some(fp => fp.action == 'Tender page/Hidden facts') && <Tab value="hiddenFacts">Dold fakta</Tab>}
              {featurePermissions?.some(fp => fp.action == 'Tender page/Notes') && <Tab value="logs">Loggbok</Tab>}
              {featurePermissions?.some(fp => fp.action == 'Tender page/Changes') && <Tab value="history">Ändringar</Tab>}
              {featurePermissions?.some(fp => fp.action == 'Tender page/Contract') && <Tab value="contract">Prisindexering</Tab>}
              {user && user.roles.some((r: any) => r.roleType === 'General') && <Tab value="permissions">Behörigheter</Tab>}
            </TabList>
            {tender && tendering && <div className="flex-1 min-h-0 overflow-y-auto p-1">
              {actualTab === 'tenderManager' && <TenderManagerModal tenderDocument={tender} tenderManagerTool={props.tenderManager} tenderingDocument={tendering} users={props.users} tags={props.tags} onChange={props.onTenderManagerChange} onFilterAdd={props.onFilterAdd} onTenderChange={(patch) => setTender(prev => prev ? { ...prev, ...patch } : prev)} />}
              {actualTab === 'info' && <TenderInfo tenderDocument={tender} tenderingDocument={tendering} isProcurer={isProcurer} updateTender={setTender} onTabChange={(tabValue, questionID) => onTabChange(tabValue, questionID)} />}
              {actualTab === 'facts' && featurePermissions?.some(fp => fp.action == 'Tender page/Facts') && <TenderFacts tenderDocument={tender} tendering={tendering} updateTender={setTender} overviewQuestionID={overviewQuestionID} />}
              {actualTab === 'logs' && <TenderLogs tenderDocument={tender} tenderingDocument={tendering} />}
              {actualTab === 'history' && <TenderHistory tenderDocument={tender} tenderingDocument={tendering} users={users} />}
              {actualTab === 'contract' && <TenderContract tenderDocument={tender} tenderingDocument={tendering} updateTender={setTender} />}
              {actualTab === 'permissions' && <TenderPermissions tenderDocument={tender} tenderingDocument={tendering} />}
              {actualTab === 'hiddenFacts' && featurePermissions?.some(fp => fp.action == 'Tender page/Hidden facts') && <TenderHiddenFacts tenderDocument={tender} tenderingDocument={tendering} updateTender={setTender} />}

            </div>}
          </div>}
          {tenderLoading && <Spinner className="mt-10" size="huge" label="Laddar..."></Spinner>}
      </DrawerBody>
    </Drawer>
  );
}

export default TenderSettingsDrawer;